import React, { useEffect, useRef, useState } from 'react';
import { container, containerPhoto, imgStyles, nothingFound, showReviewPhotoBtn, wrapper } from './style';
import { useTranslation } from 'react-i18next';
import { Typography } from 'fronton-react';
import { NothingFound } from 'common/icons/nothing-found';
import { ReviewGalleryPicture } from './review-gallery-picture';
import { SINGLE_PRODUCT } from 'common/constants';
import { GalleryPhoto, ReviewsGroupType } from 'features/reviews';
import { GalleryScrollArrows } from './gallery-scroll-arrows';
import { analyticsUtils } from 'utils/analytics-utils';

const SCROLL_STEP = 300;
const EAGER_LOAD_COUNT = 5;
const MIN_PHOTOS_FOR_ARROWS = 4;
const SCROLL_EPSILON = 50;
const LOAD_MORE_THRESHOLD = 1;

type PdpGalleryBuyerProps = {
  photos: GalleryPhoto[];
  defaultActiveTab: ReviewsGroupType;
  onShowPicture: (pic: GalleryPhoto | null, photos: GalleryPhoto[]) => void;
  fetchNextPhotos: (currentPage: number) => void;
  nextPhotosPageIndex: number;
  isPhotosLoading: boolean;
  hasMorePhotos: boolean;
};

export const PdpBuyerGallery = ({
  photos,
  defaultActiveTab,
  onShowPicture,
  fetchNextPhotos,
  nextPhotosPageIndex,
  isPhotosLoading,
  hasMorePhotos,
}: PdpGalleryBuyerProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const lastRequestedPhotosPageRef = useRef<number | null>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const photosLength = photos?.length ?? 0;

  const checkScrollPosition = () => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(Math.ceil(scrollLeft + clientWidth) < scrollWidth - LOAD_MORE_THRESHOLD);
    }
  };

  const tryLoadMorePhotos = () => {
    if (isPhotosLoading || !hasMorePhotos) return;

    const node = containerRef.current;
    if (!node) return;
    const { scrollLeft, scrollWidth, clientWidth } = node;
    const isNearRightEdge = scrollLeft + clientWidth >= scrollWidth - SCROLL_EPSILON;
    if (isNearRightEdge && lastRequestedPhotosPageRef.current !== nextPhotosPageIndex) {
      fetchNextPhotos(nextPhotosPageIndex);
      lastRequestedPhotosPageRef.current = nextPhotosPageIndex;
    }
  };

  const scrollContainer = (direction: 'left' | 'right') => {
    if (!containerRef.current) return;

    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'reviewsBlock',
      elementName: 'Фото покупателей',
      elementText: direction === 'right' ? 'switch-forward' : 'switch-backward',
    });

    const scrollAmount = direction === 'right' ? SCROLL_STEP : -SCROLL_STEP;
    containerRef.current.scrollBy({
      left: scrollAmount,
      behavior: 'smooth',
    });

    if (direction === 'right') {
      tryLoadMorePhotos();
    }
  };

  const handleScroll = () => {
    checkScrollPosition();
    tryLoadMorePhotos();
  };

  const handlePhotoClick = (photo: GalleryPhoto) => () => {
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'reviewsBlock',
      elementName: 'Фото покупателей',
      elementText: 'Photo',
    });
    onShowPicture(photo, photos);
  };

  useEffect(() => {
    lastRequestedPhotosPageRef.current = null;
  }, [defaultActiveTab]);

  useEffect(() => {
    checkScrollPosition();
  }, [photos]);

  if (photosLength === 0 && defaultActiveTab !== SINGLE_PRODUCT) return null;

  return (
    <div className={container}>
      <Typography color="text-primary" size="body_accent" variant="s">
        {t('reviews.gallery.photo_buyer')}
      </Typography>
      {photosLength === 0 && defaultActiveTab === SINGLE_PRODUCT && (
        <div className={nothingFound}>
          <NothingFound />
          <Typography color="text-secondary" size="caption" variant="minor">
            {t('reviews.gallery.no_photo')}
          </Typography>
        </div>
      )}
      {photosLength > 0 && (
        <div className={wrapper}>
          <div ref={containerRef} onScroll={handleScroll} className={containerPhoto}>
            {photos.map((item, idx) => {
              return (
                <button
                  className={showReviewPhotoBtn}
                  onClick={handlePhotoClick(item)}
                  key={item.urlOriginal}
                >
                  <ReviewGalleryPicture
                    className={imgStyles}
                    src={item.urlLarge}
                    loading={idx < EAGER_LOAD_COUNT ? 'eager' : 'lazy'}
                    alt={`${item.displayedName} - фото из отзыва ${item.authorName}`}
                    width={300}
                    height={300}
                  />
                </button>
              );
            })}
          </div>

          {photosLength > MIN_PHOTOS_FOR_ARROWS && (
            <GalleryScrollArrows
              showLeftArrow={showLeftArrow}
              showRightArrow={showRightArrow}
              onScrollLeft={() => scrollContainer('left')}
              onScrollRight={() => scrollContainer('right')}
            />
          )}
        </div>
      )}
    </div>
  );
};
